import { format } from "date-fns";
import type { TaskSurfaceCard } from "@/services/api/agentic-v2/types";
import { formatAdviserTimeGreeting } from "@/utils/adviserPersonaName";
import {
  reviewQueueClientName,
  sortReviewQueueTasks,
} from "@/components/agentic-surface/widgets/home/reviewQueueModel";

export type OpenworkStandupBrief = {
  greetingLine: string;
  dateLine: string;
  leadLine: string;
  narrative: string;
  speechText: string;
  composedAt: Date;
  celebrating?: boolean;
  priorityTask?: TaskSurfaceCard | null;
};

export type OpenworkNextMeetingBrief = {
  clientName: string;
  title: string;
  dateLabel: string;
  time: string;
};

const OPENWORK_STANDUP_PRIORITIES: Record<string, string> = {
  "ow-james-sarah-mpgi":
    "there's an open question about their emergency fund I flagged before you approve",
  "ow-michael-roberts":
    "capacity for loss still needs confirming before we send his consolidation letter",
  "ow-david-williams":
    "several blocking notes on his estate plan that are worth clearing first",
};

type QueueBreakdown = { letters: number; addendums: number; total: number };

function timeOfDayGreeting(now: Date): string {
  const hour = now.getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function isAddendumTask(task: TaskSurfaceCard): boolean {
  const ref = task.scenario_ref?.trim() ?? "";
  if (ref.includes("addendum")) return true;
  return (task.name ?? "").toLowerCase().includes("addendum");
}

export function buildOpenworkQueueBreakdown(tasks: TaskSurfaceCard[]): QueueBreakdown {
  let letters = 0;
  let addendums = 0;
  for (const row of tasks) {
    if (isAddendumTask(row)) addendums += 1;
    else letters += 1;
  }
  return { letters, addendums, total: tasks.length };
}

function plural(count: number, singular: string, pluralForm = `${singular}s`): string {
  return count === 1 ? singular : pluralForm;
}

/** Short phrase: "2 letters and 1 addendum" */
export function buildOpenworkQueueBreakdownPhrase(tasks: TaskSurfaceCard[]): string {
  const { letters, addendums, total } = buildOpenworkQueueBreakdown(tasks);
  if (total === 0) return "";
  if (total === 1) {
    return addendums === 1 ? "1 addendum" : "1 letter";
  }
  const parts: string[] = [];
  if (letters > 0) parts.push(`${letters} ${plural(letters, "letter")}`);
  if (addendums > 0) parts.push(`${addendums} ${plural(addendums, "addendum")}`);
  return parts.join(" and ");
}

/** Summary lead: "I've lined up 3 tasks for your review: 2 letters and 1 addendum." */
export function buildOpenworkQueueSummaryLine(tasks: TaskSurfaceCard[]): string {
  const { total } = buildOpenworkQueueBreakdown(tasks);
  if (total === 0) return "Nothing waiting on you right now.";
  if (total === 1) {
    const phrase = buildOpenworkQueueBreakdownPhrase(tasks);
    return `I've lined up ${phrase} for your review.`;
  }
  const breakdown = buildOpenworkQueueBreakdownPhrase(tasks);
  return `I've lined up ${total} tasks for your review: ${breakdown}.`;
}

function buildOpenworkProgressLeadLine(completedCount: number): string {
  const taskWord = plural(completedCount, "task");
  return `Good work. You've completed ${completedCount} ${taskWord} today.`;
}

/** Joint cases: "James & Sarah Thompson" → "James & Sarah". Individuals keep first name. */
export function openworkClientShortName(displayName: string): string {
  const trimmed = displayName.trim();
  if (!trimmed) return trimmed;
  const parts = trimmed.split(/\s+/);
  const ampIndex = parts.indexOf("&");
  if (ampIndex >= 1 && ampIndex + 1 < parts.length) {
    return parts.slice(0, ampIndex + 2).join(" ");
  }
  return parts[0] ?? trimmed;
}

/** Priority detail — second line, not a repeat of the queue summary. */
export function buildOpenworkStandupLeadLine(firstTask: TaskSurfaceCard): string {
  const clientDisplay = reviewQueueClientName(firstTask) ?? "your first client";
  const clientShort = openworkClientShortName(clientDisplay);
  const scenarioRef = firstTask.scenario_ref?.trim();
  const priorityReason = scenarioRef ? OPENWORK_STANDUP_PRIORITIES[scenarioRef] : null;
  if (priorityReason) {
    return `I'd start with ${clientShort}. ${priorityReason.charAt(0).toUpperCase()}${priorityReason.slice(1)}.`;
  }
  return `I'd start with ${clientShort}. Their letter has open questions I'd like you to look at first.`;
}

export function pickOpenworkPriorityTask(tasks: TaskSurfaceCard[]): TaskSurfaceCard | null {
  const active = sortReviewQueueTasks(
    tasks.filter((task) => task.status !== "queued"),
    null,
  );
  if (active.length === 0) return null;
  return active.find((task) => !isAddendumTask(task)) ?? active[0] ?? null;
}

function pickPriorityTask(tasks: TaskSurfaceCard[]): TaskSurfaceCard {
  return pickOpenworkPriorityTask(tasks) ?? tasks[0];
}

function buildOpenworkQueueNarrative(tasks: TaskSurfaceCard[], completedCount: number): string {
  const breakdown = buildOpenworkQueueBreakdownPhrase(tasks);
  const priorityLine = buildOpenworkStandupLeadLine(pickPriorityTask(tasks));
  if (completedCount > 0) {
    return `Left in the queue: ${breakdown}. ${priorityLine}`;
  }
  return priorityLine;
}

function buildActiveQueueBrief(
  tasks: TaskSurfaceCard[],
  completedCount: number,
): Pick<OpenworkStandupBrief, "leadLine" | "narrative"> {
  if (completedCount > 0) {
    return {
      leadLine: buildOpenworkProgressLeadLine(completedCount),
      narrative: buildOpenworkQueueNarrative(tasks, completedCount),
    };
  }
  return {
    leadLine: buildOpenworkQueueSummaryLine(tasks),
    narrative: buildOpenworkStandupLeadLine(pickPriorityTask(tasks)),
  };
}

export function buildOpenworkStandupBrief(
  personaName: string,
  needsMeTasks: TaskSurfaceCard[],
  loading: boolean,
  options?: {
    allLettersApproved?: boolean;
    approvedCount?: number;
    nextMeeting?: OpenworkNextMeetingBrief | null;
  },
  now = new Date(),
): OpenworkStandupBrief {
  const greetingLine = formatAdviserTimeGreeting(timeOfDayGreeting(now), personaName);
  const dateLine = format(now, "EEEE d MMMM");
  const completedCount = options?.approvedCount ?? 0;
  const tasks = sortReviewQueueTasks(
    needsMeTasks.filter((task) => task.status !== "queued"),
    null,
  );

  if (loading && tasks.length === 0) {
    const narrative = "Give me a moment. I'm pulling together your review queue.";
    return {
      greetingLine,
      dateLine,
      leadLine: "Getting your letters ready…",
      narrative,
      speechText: `${greetingLine} ${narrative}`,
      composedAt: now,
    };
  }

  if (tasks.length === 0) {
    if (options?.allLettersApproved) {
      const meeting = options.nextMeeting;
      const meetingLine = meeting
        ? `Tomorrow you've got ${openworkClientShortName(meeting.clientName)} at ${meeting.time} (${meeting.title.toLowerCase()}).`
        : "I'll keep an eye on your diary and anything that needs your attention.";
      const narrative = meeting
        ? `${meetingLine} I've put the briefing together already. Looks like it's on Teams, so I'll pull the recording through when you're done. If you switch to in-person, you can record or upload notes from the meeting card. I'll also watch for rate changes and anything else that affects your open cases.`
        : "You're clear on letters for now. I'll keep monitoring your meetings, client news, and anything else that needs your attention.";
      const trimmedPersona = personaName.trim();
      const leadLine = trimmedPersona
        ? `Nice work, ${trimmedPersona}. You've cleared every letter in the queue.`
        : "Nice work. You've cleared every letter in the queue.";
      return {
        greetingLine,
        dateLine,
        leadLine,
        narrative,
        speechText: `${greetingLine} ${leadLine} ${narrative}`,
        composedAt: now,
        celebrating: true,
      };
    }

    if (completedCount > 0) {
      const leadLine = buildOpenworkProgressLeadLine(completedCount);
      const narrative = "You're all caught up on letters for now.";
      return {
        greetingLine,
        dateLine,
        leadLine,
        narrative,
        speechText: `${greetingLine} ${leadLine} ${narrative}`,
        composedAt: now,
      };
    }

    const narrative = "You're all caught up on letters for now.";
    return {
      greetingLine,
      dateLine,
      leadLine: "Nothing waiting on you right now.",
      narrative,
      speechText: `${greetingLine} ${narrative}`,
      composedAt: now,
    };
  }

  const { leadLine, narrative } = buildActiveQueueBrief(tasks, completedCount);

  return {
    greetingLine,
    dateLine,
    leadLine,
    narrative,
    speechText: `${greetingLine} ${leadLine} ${narrative}`,
    composedAt: now,
    priorityTask: pickPriorityTask(tasks),
  };
}
